import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { PlacePreviewFeature } from "../lib/api";

const PLACE_PARAM = "place";
const PREVIEW_STATE_KEY = "previewPlace";
const ORIGIN_STATE_KEY = "originPlaceId";

/** History-state key for the active detail tab. Bound to the navigation entry, so each place view restores its own tab on back/forward and reload, and resets when reopened. */
export const PLACE_TAB_STATE_KEY = "placeTab";

/** Coordinate tolerance for equality (~1m at equator) */
const COORD_EPSILON = 0.00001;

/** Check if two preview places are the same (by name and coordinates) */
function isSamePreviewPlace(a: PlacePreviewFeature, b: PlacePreviewFeature): boolean {
  return (
    a.properties.name === b.properties.name &&
    Math.abs(a.geometry.coordinates[0] - b.geometry.coordinates[0]) < COORD_EPSILON &&
    Math.abs(a.geometry.coordinates[1] - b.geometry.coordinates[1]) < COORD_EPSILON
  );
}

interface UsePlaceNavigationOptions {
  /** Current selected place ID (from state) */
  selectedPlaceId: string | null;
  /**
   * Callback when navigation changes (browser back/forward or initial load).
   * - `string`: place ID from URL, consumer looks up the feature
   * - `PlacePreviewFeature`: preview from history state, use directly
   * - `null`: no selection
   */
  onPlaceChange: (data: string | PlacePreviewFeature | null) => void;
}

interface NavigateOptions {
  /** Replace current history entry instead of pushing a new one */
  replace?: boolean;
  /** Origin place ID for "back to origin" navigation */
  originPlaceId?: string;
  /** Force preview mode (state-only, no URL param) even for places with IDs */
  preview?: boolean;
}

interface UsePlaceNavigationReturn {
  /** Navigate to a place (pushes or replaces history entry with URL param or state) */
  navigateToPlace: (place: PlacePreviewFeature, options?: NavigateOptions) => void;
  /** Clear selection (pushes history entry without place param) */
  clearPlaceSelection: () => void;
  /** Origin place ID from history state (for "back to origin" navigation) */
  originPlaceId: string | undefined;
}

/**
 * Syncs place selection with browser history for back/forward support.
 *
 * For real places (with ID): uses URL param `?place=<id>`
 * For preview places (no ID): uses history state to avoid polluting URLs
 *
 * - On mount: reads URL/state and calls `onPlaceChange`
 * - On browser back/forward: syncs via `onPlaceChange`
 * - `navigateToPlace(feature)`: pushes history entry
 * - `clearPlaceSelection()`: pushes URL without place param
 */
export function usePlaceNavigation({
  selectedPlaceId,
  onPlaceChange,
}: UsePlaceNavigationOptions): UsePlaceNavigationReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const onPlaceChangeRef = useRef(onPlaceChange);
  onPlaceChangeRef.current = onPlaceChange;

  // On mount and URL/state changes: sync to state
  useEffect(() => {
    const urlPlaceId = searchParams.get(PLACE_PARAM);
    const previewPlace = location.state?.[PREVIEW_STATE_KEY] as PlacePreviewFeature | undefined;

    // Determine what changed
    const currentSelection = urlPlaceId || (previewPlace ? "" : null);
    const selectedId = selectedPlaceId ?? null;

    // On initial mount, always sync
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (previewPlace) {
        onPlaceChangeRef.current(previewPlace);
      } else if (urlPlaceId !== selectedId) {
        onPlaceChangeRef.current(urlPlaceId);
      }
      return;
    }

    // After mount: check for changes
    if (previewPlace) {
      onPlaceChangeRef.current(previewPlace);
    } else if (currentSelection !== selectedId) {
      onPlaceChangeRef.current(urlPlaceId);
    }
  }, [searchParams, location.state, selectedPlaceId]);

  const navigateToPlace = useCallback(
    (place: PlacePreviewFeature, options?: NavigateOptions) => {
      const placeId = place.properties.id;
      const replace = options?.replace ?? false;
      const originPlaceId = options?.originPlaceId;

      // Build state with origin if provided
      const buildState = () => {
        const state = { ...location.state };
        delete state[PLACE_TAB_STATE_KEY];
        if (originPlaceId) {
          state[ORIGIN_STATE_KEY] = originPlaceId;
        } else {
          delete state[ORIGIN_STATE_KEY];
        }
        return state;
      };

      if (placeId && !options?.preview) {
        // Real place: use URL param
        // Dedupe: don't push if already at this place
        const currentPlaceId = new URLSearchParams(location.search).get(PLACE_PARAM);
        if (currentPlaceId === placeId) return;

        const state = buildState();
        delete state[PREVIEW_STATE_KEY];

        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(PLACE_PARAM, placeId);
            return next;
          },
          { replace, state },
        );
      } else {
        // Preview place: use history state
        // Dedupe: don't push if same preview already in state
        const currentPreview = location.state?.[PREVIEW_STATE_KEY] as PlacePreviewFeature | undefined;
        if (currentPreview && isSamePreviewPlace(currentPreview, place)) return;

        const params = new URLSearchParams(location.search);
        if (!originPlaceId) {
          // No origin (e.g. Mapbox preview) - clear place param
          params.delete(PLACE_PARAM);
        }
        // With origin (e.g. related place) - keep existing place param
        const search = params.toString();
        navigate(location.pathname + (search ? `?${search}` : ""), {
          state: { ...buildState(), [PREVIEW_STATE_KEY]: place },
          replace,
        });
      }
    },
    [setSearchParams, navigate, location.pathname, location.search, location.state],
  );

  const clearPlaceSelection = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete(PLACE_PARAM);
    const search = params.toString();

    // Clear preview state if present
    const hasPreviewState = location.state?.[PREVIEW_STATE_KEY];
    if (hasPreviewState) {
      const newState = { ...location.state };
      delete newState[PREVIEW_STATE_KEY];
      navigate(location.pathname + (search ? `?${search}` : ""), {
        state: Object.keys(newState).length ? newState : undefined,
      });
    } else {
      setSearchParams(params);
    }
  }, [setSearchParams, navigate, location.pathname, location.search, location.state]);

  const originPlaceId = location.state?.[ORIGIN_STATE_KEY] as string | undefined;

  return { navigateToPlace, clearPlaceSelection, originPlaceId };
}
